import React, { useId, useLayoutEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import { FREEZE_FOR_CAPTURE } from '@components/motion/scroll-motion';
import { EnactArt } from './wee-art';
import { createStoryRenderer } from './wee-render';
import { ALIVE_AT, CUE } from './wee-timeline';
import { createFrameCadence } from './frame-cadence';

/*
 * The loop: from a moment before Enact to the story's wrap — the house
 * growing, coming alive in gold, lightening to white — then on into the
 * next Wish's first two seconds, where it takes its gold back. That
 * ends in the pose it began in, so the loop has no seam.
 */
const LOOP_FROM = CUE.enact - 1.0;
const TO_WRAP = CUE.loop - LOOP_FROM;
const GOLD_BACK = 2.0;
const LOOP_S = TO_WRAP + GOLD_BACK;
/** Reduced motion holds the house just after it comes alive. */
const STILL = ALIVE_AT + 0.6;

function loopTime(u: number): number {
  return u < TO_WRAP ? LOOP_FROM + u : CUE.wish + (u - TO_WRAP);
}

/**
 * The Enact card's picture on its own, looping: the house coming alive,
 * forever. Plays only while on screen.
 */
export const EnactLoop: React.FC<{ viewBox?: string; className?: string }> = ({ viewBox, className }) => {
  const reduced = useReducedMotion() ?? false;
  const still = reduced || FREEZE_FOR_CAPTURE;
  const id = `enact${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const renderer = createStoryRenderer(root);
    if (still) {
      renderer.render(STILL, false);
      return undefined;
    }

    const cadence = createFrameCadence();
    let u = 0;
    let raf = 0;
    renderer.render(loopTime(u), false);
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const step = cadence.tick(now);
      if (step === null) return;
      u = (u + step) % LOOP_S;
      renderer.render(loopTime(u), false);
    };
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry.isIntersecting && !raf) {
        cadence.reset();
        raf = requestAnimationFrame(frame);
      } else if (!entry.isIntersecting && raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    });
    observer.observe(root);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [still]);

  return (
    <div ref={ref} className={className}>
      <EnactArt id={id} viewBox={viewBox} />
    </div>
  );
};
